import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

/* ---------- Symbol aus dem Satz des Prototyps ---------- */
export function Symbol({ id, klein = false }: { id: string; klein?: boolean }) {
  return <svg className={klein ? 'i i-sm' : 'i'} aria-hidden="true"><use href={`#i-${id}`} /></svg>;
}

/** CSS-Variablen als Stil (z. B. --n, --i für Segmente). */
export const vars = (v: Record<string, string | number>) => v as CSSProperties;

/* ---------- Segment mit gleitender Marke ---------- */
export interface SegmentWahl<T extends string> { wert: T; inhalt: ReactNode }

export function Segment<T extends string>({ wahl, wert, setzen, klein = false, label, id, rolle = 'group' }: {
  wahl: SegmentWahl<T>[]; wert: T; setzen: (w: T) => void; klein?: boolean; label: string; id?: string; rolle?: 'group' | 'nav';
}) {
  const i = Math.max(0, wahl.findIndex(w => w.wert === wert));
  const Huelle = rolle === 'nav' ? 'nav' : 'div';
  return (
    <Huelle className={klein ? 'seg seg-sm' : 'seg'} id={id} style={vars({ '--n': wahl.length, '--i': i })} role={rolle === 'group' ? 'group' : undefined} aria-label={label}>
      <span className="seg-thumb" aria-hidden="true" />
      {wahl.map(w => (
        <button key={w.wert} type="button" aria-pressed={w.wert === wert} onClick={() => w.wert !== wert && setzen(w.wert)}>{w.inhalt}</button>
      ))}
    </Huelle>
  );
}

/* ---------- Hinweise unten rechts ---------- */
interface Hinweis { id: number; text: string; an: boolean }
const HinweisKontext = createContext<(text: string) => void>(() => undefined);
export const useHinweis = () => useContext(HinweisKontext);

export function Hinweise({ children }: { children: ReactNode }) {
  const [liste, setListe] = useState<Hinweis[]>([]);
  const zaehler = useRef(0);
  const zeigen = useCallback((text: string) => {
    const id = ++zaehler.current;
    setListe(l => [...l, { id, text, an: false }]);
    requestAnimationFrame(() => requestAnimationFrame(() => setListe(l => l.map(h => h.id === id ? { ...h, an: true } : h))));
    setTimeout(() => {
      setListe(l => l.map(h => h.id === id ? { ...h, an: false } : h));
      setTimeout(() => setListe(l => l.filter(h => h.id !== id)), 200);
    }, 3400);
  }, []);
  return (
    <HinweisKontext.Provider value={zeigen}>
      {children}
      <div className="toasts" id="toasts" aria-live="polite">
        {liste.map(h => <div key={h.id} className={h.an ? 'toast show' : 'toast'}><Symbol id="check" klein /><span>{h.text}</span></div>)}
      </div>
    </HinweisKontext.Provider>
  );
}

/* ---------- Leerzustand mit nächstem Schritt ---------- */
export function Leer({ symbol, titel, children }: { symbol: string; titel: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <span className="empty-icon"><Symbol id={symbol} /></span>
      <h2>{titel}</h2>
      {children}
    </div>
  );
}

/** Seitenkopf wie im Prototyp: Titel, Zusatz, rechts höchstens ein Element. */
export function SeitenKopf({ titel, zusatz, id, children }: { titel: string; zusatz?: ReactNode; id?: string; children?: ReactNode }) {
  return (
    <header className="page-head">
      <div><h1 id={id}>{titel}</h1>{zusatz !== undefined && <p className="meta">{zusatz}</p>}</div>
      {children}
    </header>
  );
}

/** Setzt die Lage der gleitenden Marke in einer Seitenleiste auf den gewählten Punkt. */
export function useNavMarke(abhaengig: unknown) {
  const nav = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const setzen = () => {
      const el = nav.current, cur = el?.querySelector<HTMLElement>('[aria-current="page"]'), ind = el?.querySelector<HTMLElement>('.nav-ind');
      if (cur && ind) ind.style.setProperty('--y', cur.offsetTop + 'px');
    };
    setzen();
    addEventListener('resize', setzen);
    return () => removeEventListener('resize', setzen);
  }, [abhaengig]);
  return nav;
}

/** Schließt bei Escape. */
export function useEscape(an: boolean, schliessen: () => void) {
  useEffect(() => {
    if (!an) return;
    const taste = (e: KeyboardEvent) => { if (e.key === 'Escape') schliessen(); };
    document.addEventListener('keydown', taste);
    return () => document.removeEventListener('keydown', taste);
  }, [an, schliessen]);
}
